import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Coins, Users as UsersIcon, Calendar, Copy, Check, Save } from "lucide-react";
import { toast } from "sonner";
import { useManageCompany } from "./ManageCompanyContext";

export default function Overview({ details }) {
    const { t } = useTranslation();
    const { refresh } = useManageCompany();
    const [name, setName] = useState(details?.name || "");
    const [copied, setCopied] = useState(false);
    const { call: updateName, isLoading: isSaving } = useBluelagoon({ method: "updateCompanyName" });

    useEffect(() => { setName(details?.name || ""); }, [details?.name]);

    if (!details) return null;

    const dirty = name.trim() !== (details.name || "").trim() && name.trim().length > 0;

    const handleSave = async () => {
        const res = await updateName({ companyId: details.id, name: name.trim() }).catch(() => null);
        if (res?.status === 200) {
            toast.success(t("admin.manageCompany.overview.nameSaved", "Company name updated"));
            refresh();
        } else {
            toast.error(res?.error || t("admin.manageCompany.overview.nameSaveError", "Failed to update name"));
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(details.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-4 space-y-4">
                    <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wide">{t("admin.manageCompany.overview.companyId", "Company ID")}</label>
                        <div className="flex items-center gap-2 mt-1">
                            <code className="flex-1 px-3 py-2 rounded bg-muted text-xs font-mono">{details.id}</code>
                            <Button variant="ghost" size="icon" onClick={handleCopy}>
                                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                            </Button>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wide">{t("admin.manageCompany.overview.companyName", "Company Name")}</label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
                            <Button onClick={handleSave} disabled={!dirty || isSaving}>
                                <Save className="h-4 w-4 mr-1" />{t("common.save", "Save")}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("admin.manageCompany.overview.currentCredits", "Current Credits")}</p>
                    <p className="text-2xl font-semibold flex items-center gap-2 mt-1"><Coins className="h-5 w-5 text-yellow-500" />{(details.currentCredits || 0).toFixed(2)}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("admin.manageCompany.overview.users", "Users")}</p>
                    <p className="text-2xl font-semibold flex items-center gap-2 mt-1"><UsersIcon className="h-5 w-5 text-blue-500" />{details.userCount || 0}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("admin.manageCompany.overview.subscription", "Subscription")}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <Badge variant={details.subscriptionStatus === "ACTIVE" ? "default" : "secondary"}>
                            {details.subscriptionStatus || t("admin.manageCompany.overview.noSubscription", "No subscription")}
                        </Badge>
                        {details.planName && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                {details.planName}
                            </Badge>
                        )}
                    </div>
                </CardContent></Card>
            </div>
            <Card><CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t("admin.manageCompany.overview.createdAt", "Created")}:</span>
                    <span>{details.createdAt ? new Date(details.createdAt).toLocaleDateString() : "—"}</span>
                </div>
                <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t("admin.manageCompany.overview.trialEnd", "Trial ends")}:</span>
                    <span>{details.trialEndDate ? new Date(details.trialEndDate).toLocaleDateString() : "—"}</span>
                </div>
            </CardContent></Card>
        </div>
    );
}
